import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { TFunction } from 'i18next';
import { useLocalStorage } from 'usehooks-ts';

import { createHandle, createLoader } from 'services/breadcrumbs/breadcrumbs';
import { useMessage } from 'services/repositories/messages/messages';
import { useCurrentPartner } from 'services/repositories/partners/partners';
import { usePaymentReconciliationDetail } from 'services/repositories/paymentReconciliation/paymentReconciliation';
import { EMPTY_FIELD } from 'support/helpers/const/const';
import { routeToPage } from 'support/helpers/navigation/navigation';
import { RouteParams } from 'support/helpers/types/types';
import { PaymentReconciliationRoutes } from 'support/types';
import { Page } from 'components/Page/Page';

import { SubmitToErpAction } from '../components/SubmitToErpAction/SubmitToErpAction';
import { PaymentReconciliationLineItems } from './components/PaymentReconciliationLineItems/PaymentReconciliationLineItems';
import { PaymentReconciliationDetailsSidebar } from './components/PaymentReconciliationSidebar/PaymentReconciliationSidebar';
import { PaymentReconciliationTopBanner } from './components/PaymentReconciliationTopBanner/PaymentReconciliationTopBanner';
import { PaymentReconciliationDetailsData } from './types';

const ACTION_REQUIRED_STATUS = ['PENDING_REVIEW', 'MISSING'];
const PAYMENT_RECONCILIATION_PENDING_LINES_FILTER = 'PAYMENT_RECONCILIATION_PENDING_LINES_FILTER';

export const PaymentReconciliationDetails = () => {
  const params = useParams<RouteParams<PaymentReconciliationRoutes.detail>>();
  const [showOnlyPendingLines, setShowOnlyPendingLines] = useLocalStorage<boolean>(
    PAYMENT_RECONCILIATION_PENDING_LINES_FILTER,
    false,
  );
  const { data: currentPartner, isLoading: isLoadingCurrentPartner } = useCurrentPartner();
  const { data: messageData, isLoading: isLoadingMessage } = useMessage({ variables: { messageId: params.id } });
  const {
    data: paymentReconciliationData,
    isFetching: isFetchingPaymentReconciliationDetails,
    isLoading: isLoadingPaymentReconciliationDetails,
  } = usePaymentReconciliationDetail({
    variables: { id: (params as RouteParams<PaymentReconciliationRoutes.detail>).id },
  });

  const isLoading = isLoadingMessage || isLoadingCurrentPartner || isLoadingPaymentReconciliationDetails;

  const hasPendingLines = useMemo(() => {
    return Boolean(
      paymentReconciliationData?.data.line_items.some((item) => ACTION_REQUIRED_STATUS.includes(item.status)),
    );
  }, [paymentReconciliationData?.data.line_items]);

  const hasMultiplePartners = useMemo(() => {
    const partners = new Set(paymentReconciliationData?.data.line_items.map((item) => item.trade_partner.id));
    return partners.size > 1;
  }, [paymentReconciliationData?.data.line_items]);

  const lineItems = useMemo(
    () =>
      paymentReconciliationData?.data.line_items.filter((item) => {
        if (showOnlyPendingLines && hasPendingLines) {
          return ACTION_REQUIRED_STATUS.includes(item.status);
        }
        return true;
      }) || [],
    [hasPendingLines, paymentReconciliationData?.data.line_items, showOnlyPendingLines],
  );

  const submitToErpAction = paymentReconciliationData?.data.receiver_envelope.canForceDeliver ? (
    <SubmitToErpAction
      receiverEnvelope={paymentReconciliationData.data.receiver_envelope}
      size="small"
      numberOfDocuments={paymentReconciliationData.data.line_items.length}
    />
  ) : null;

  return (
    <Page isLoading={isLoading}>
      <Page.Head rightContent={submitToErpAction} />
      <Page.Section
        topBanner={
          <PaymentReconciliationTopBanner
            blockingError={messageData?.data.blocking_error}
            hasPendingItems={hasPendingLines}
            isFilteringPendingOnly={showOnlyPendingLines}
            onTogglePendingFilter={() => setShowOnlyPendingLines((checked) => !checked)}
          />
        }
        rightAside={
          <PaymentReconciliationDetailsSidebar
            message={messageData?.data}
            currentPartner={currentPartner}
            paymentReconciliation={paymentReconciliationData}
          />
        }
      >
        {paymentReconciliationData ? (
          <PaymentReconciliationLineItems
            isLoading={isFetchingPaymentReconciliationDetails}
            data={lineItems}
            hasMultiplePartners={hasMultiplePartners}
            currency={paymentReconciliationData.data.currency} // we assume all line items have the same currency
          />
        ) : null}
      </Page.Section>
    </Page>
  );
};

PaymentReconciliationDetails.loader = createLoader(
  ({ params }) => usePaymentReconciliationDetail.getKey({ id: params.id }),
  usePaymentReconciliationDetail.queryFn,
);

PaymentReconciliationDetails.handle = createHandle<
  PaymentReconciliationDetailsData,
  RouteParams<PaymentReconciliationRoutes.detail>
>((params, data) => ({
  label: (t: TFunction) =>
    t('paymentReconciliation:detailPage.breadcrumbs', {
      externalReference: data?.data.external_message_ref || EMPTY_FIELD,
    }),
  to: routeToPage(PaymentReconciliationRoutes.detail, { id: params.id }),
  key: `payment-reconciliation-detail`,
}));
